import { type Dispatch, type SetStateAction, useCallback } from "react";
import { emitter } from "src/state/events.ts";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import type {
  UseQueryResult,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";

export const useResourceFormRefresh = <
  FormValues extends FieldValues = FieldValues,
>({
  resource,
  id,
  action,
  clearStorage,
  form,
  initialValues,
  refetch,
  refetchTranslations,
  refetchFormConfig,
  setRefreshCount,
}: {
  resource: string;
  form: UseFormReturn<FormValues>;
  initialValues: FormValues;
  id: string | undefined;
  action: "edit" | "create";
  refetch: UseQueryResult<any>["refetch"];
  refetchTranslations: UseSuspenseQueryResult<any>["refetch"];
  refetchFormConfig: UseSuspenseQueryResult<any>["refetch"];
  clearStorage: () => void;
  setRefreshCount: Dispatch<SetStateAction<number>>;
}) => {
  return useCallback(
    async (
      params: {
        resetForm?: boolean;
        revalidateData?: boolean;
      } = {},
    ) => {
      const { resetForm = true, revalidateData = true } = params;
      if (!resetForm && !revalidateData) {
        throw new Error("at least resetForm or revalidateData should be true");
      }
      emitter.emit("formWillRefresh", {
        resource,
        id,
        type: action,
      });

      if (resetForm && revalidateData) {
        clearStorage();
        await refetch();
      } else if (resetForm) {
        form.reset(initialValues);
        clearStorage();
      } else if (revalidateData) {
        await refetch();
      }
      if (import.meta.env.DEV) {
        await Promise.all([refetchTranslations(), refetchFormConfig()]);
      }
      setRefreshCount((prev) => prev + 1);
    },
    [
      refetchFormConfig,
      refetchTranslations,
      setRefreshCount,
      refetch,
      clearStorage,
      form,
      initialValues,
      resource,
      id,
      action,
    ],
  );
};
